<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import {
  GetCartFromLocalStorageOrApi,
  DeleteItemFromCart,
  UpdateItemInCart,
  CartLocalStorageKey,
    GetUniqueProductCodes,
} from '../services/CartService'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { NumericTextBox as KNumericTextBox } from '@progress/kendo-vue-inputs'
import AoeCartPopupConsultantLocator from './AoeCartPopupConsultantLocator.vue'

import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import { ref, computed, watch, onMounted } from 'vue'
import type { IAoeCart, IAoeCartItem } from '../types/CartTypes'
import {
  GetFullProductImageUrl,
  GetFullProductUrl,
  GetFullProductImageDictionary
} from '../services/SkuService.ts'
import {
  GetCartDetailUrl,
  GetCheckoutUrl,
  GetLoginUrl,
} from '../stores/SettingsStore.ts'
import { Get } from '@/api/Common.ts'
import Swal from 'sweetalert2'
import { handleError } from '../services/ErrorService.ts'

const logger = useLogger()
setStoreLogger(logger)

const props = defineProps<{
  ispopover?: boolean
}>()
const productImageUrls = ref<Record<string, string>>({})
const emit = defineEmits(['closePopup'])
const fullProductUrlByProductCode = ref<Record<string, string>>({})
const cart = ref<IAoeCart | null>(null)
const cartDetailUrl = ref('')


const urlMap = ref<{ [key: string]: { url: string; imageUrl: string } }>({})

async function updateProductUrls() {
  if (!cart.value) return
  const newUrls: { [key: string]: { url: string; imageUrl: string } } = {}

  for (const item of cart.value.items) {
    newUrls[item.productCode] = {
      url: await GetFullProductUrl(item.productCode, 'product'),
      imageUrl: await GetFullProductImageUrl(item.productCode, 'product')
    }
  }

  urlMap.value = newUrls
}

watch(() => cart.value?.items, updateProductUrls, { immediate: true })

async function Init() {
  try {

    const cartJson = localStorage.getItem(CartLocalStorageKey)
    if (cartJson && cartJson !== 'undefined') {
      const localCart = JSON.parse(cartJson)
          const uniqueProductCodes = GetUniqueProductCodes(localCart)
      if (localCart?.aoeCartIdToken) {
        // This will get from API if local storage is stale
        const newCart = await GetCartFromLocalStorageOrApi(localCart.aoeCartIdToken)
        cart.value = newCart

        GetCartDetailUrl().then(url => {
          cartDetailUrl.value = url
        })
      }

    logger.debug('uniqueProductCodes=', uniqueProductCodes)
    productImageUrls.value =
      await GetFullProductImageDictionary(uniqueProductCodes)
    logger.debug('productImageUrls', productImageUrls)
    }
  } catch (error) {
    logger.error('Error in Init:', error)
  }
}

onMounted(() => {
  Init()
})

// Storage event handler to keep cart in sync across tabs
function storageHandler(event: StorageEvent) {
  logger.debug('AoeKitCartDetail','storageHandler', event)
  if (event.key === CartLocalStorageKey && event.newValue) {
    try {
      const localCart = JSON.parse(event.newValue)
      if (localCart?.aoeCartIdToken) {
        GetCartFromLocalStorageOrApi(localCart.aoeCartIdToken).then(newCart => {
          cart.value = newCart
        })
      }
    } catch (error) {
      logger.error('Error parsing cart from storage:', error)
    }
  }
}

window.addEventListener('storage', storageHandler, false)

function GetAvailableQty(cartItem: IAoeCartItem, currentCartQty: number) {
  if (cartItem.inventoryLevel && cartItem.inventoryLevel.availableQty) {
    return cartItem.inventoryLevel.availableQty + currentCartQty
  }
  return 999
}

function plusClickedHandler(lineNumber: number) {
  if (!cart.value) return

  const cartItem = cart.value.items[lineNumber]
  const availableQty = GetAvailableQty(cartItem, cartItem.quantity)

  if (cartItem.quantity < availableQty) {
    const newQty = cartItem.quantity + 1
    const cartIdToken = localStorage.getItem('aoeCartIdToken') || ''
    UpdateItemInCart({ lineNumber: cartItem.lineNumber, quantity: newQty, aoeCartIdToken: cartIdToken }).then(() => {
      GetCartFromLocalStorageOrApi(cartIdToken).then(newCart => {
        cart.value = newCart
      })
    }).catch(async (error) => {
      logger.error('plusClickedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    })
  }
}

function GetFullProductUrlFromStringDictionary(productCode: string): string {
  const fullProductUrl = fullProductUrlByProductCode.value[productCode]
  if (fullProductUrl) {
    logger.debug('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  } else {
    console.warn('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  }
  return fullProductUrl || '#'
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    productImageUrls,
  )
  const productImageUrl = productImageUrls.value[productCode]
  logger.debug('productImageUrl=', productImageUrl)
  if (productImageUrl) return productImageUrl
  return '#'
}

async function minusClickedHandler(lineNumber: number) {
  if (!cart.value) return

  const cartItem = cart.value.items[lineNumber]
  if (cartItem.quantity > 1) {
    const newQty = cartItem.quantity - 1
    const cartIdToken = localStorage.getItem('aoeCartIdToken') || ''
    try {
      await UpdateItemInCart({ lineNumber: cartItem.lineNumber, quantity: newQty, aoeCartIdToken: cartIdToken })
    } catch (error) {
      logger.error('minusClickedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }
    cart.value = await GetCartFromLocalStorageOrApi(cartIdToken)
  }
}

function deleteItemClickedHandler(lineNumber: number) {
  if (!cart.value) return

  const cartItem = cart.value.items[lineNumber]
  const cartIdToken = localStorage.getItem('aoeCartIdToken') || ''
  DeleteItemFromCart({ lineNumber: cartItem.lineNumber, aoeCartIdToken: cartIdToken }).then(() => {
    GetCartFromLocalStorageOrApi(cartIdToken).then(newCart => {
      cart.value = newCart

      if (cartItem) {
        Swal.fire({
          title: t('cart.ItemRemovedFromCart', { skuName: cartItem.skuName }),
          icon: 'info',
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        })
      }
    })
  })
}

function cartItemQuantityChangedHandler(event: any, cartItem: IAoeCartItem) {
  if (event && event.value !== undefined) {
    const newQty = event.value
    const availableQty = GetAvailableQty(cartItem, cartItem.quantity)

    if (newQty > 0 && newQty <= availableQty) {
      UpdateItemInCart({ lineNumber: cartItem.lineNumber, quantity: newQty, aoeCartIdToken: localStorage.getItem('aoeCartIdToken') || '' }).then(() => {
        GetCartFromLocalStorageOrApi(localStorage.getItem('aoeCartIdToken') || '').then(newCart => {
          cart.value = newCart
        })
      }).catch(async (error) => {
        logger.error('cartItemQuantityChangedHandler UpdateItemInCart failed', error)
        await handleError(error, t)
      })
    }
  }
}

function viewCartClickedHandler() {
  if (cartDetailUrl.value) {
    window.location.href = cartDetailUrl.value
  }
}

function checkoutClickedHandler() {
  GetCheckoutUrl().then(checkoutUrl => {
    window.location.href = checkoutUrl
  })
}

function signInClickedHandler() {
  GetLoginUrl().then(url => {
    window.location.href = url
  })
}

function closePopupHandler() {
  if (props.ispopover) {
    emit('closePopup')
  }
}


</script>

<template>
  <div class="aoe-kit-cart-detail container d-flex flex-row">


    <div class="cart" v-if="cart">
       <p class="pageHeader">{{ t('cart.CartHeader') }}</p>
      <div :class="props.ispopover ? 'col-12 allCartItems' : ' allCartItems'">
        <div v-for="(cartItem, index) in cart.items" :key="cartItem.skuCode" class="cartItem">
          <!-- Mobile View -->
          <div class="row mobileCartItems d-block d-md-none">
            <div class="product-image col-3">
              <a
                :href="
                  GetFullProductUrlFromStringDictionary(cartItem.productCode)
                "
                :data-category-code="cartItem.productCode"
              >
                <img
                  :src="
                    GetFullProductImageUrlFromStringDictionary(
                      cartItem.productCode,
                    )
                  "
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                />
              </a>
            </div>
            <div class="product-info col-9">
              <div class="product-name">
                <a :href="urlMap[cartItem.productCode]?.url">{{ cartItem.skuName }}</a>
              </div>
              <div class="product-sku">{{ cartItem.skuCode }}</div>
              <div class="product-price">${{ cartItem.price.toFixed(2) }}</div>
              <div class="quantity-controls">
                <KButton @click="minusClickedHandler(index)" class="quantity-btn">-</KButton>
                <KNumericTextBox
                  v-model="cartItem.quantity"
                  :min="1"
                  :max="GetAvailableQty(cartItem, cartItem.quantity)"
                  @change="cartItemQuantityChangedHandler($event, cartItem)"
                />
                <KButton @click="plusClickedHandler(index)" class="quantity-btn">+</KButton>
                <KButton @click="deleteItemClickedHandler(index)" class="delete-btn">×</KButton>
              </div>
              <div class="item-total">${{ (cartItem.price * cartItem.quantity).toFixed(2) }}</div>
            </div>
          </div>

          <!-- Desktop View -->
          <div v-if="!props.ispopover" class="row nonMobileCartItems d-none d-md-flex">
            <div class="product-image col-3">
              <a
                :href="
                  GetFullProductUrlFromStringDictionary(cartItem.productCode)
                "
                :data-category-code="cartItem.productCode"
              >
                <img
                  :src="
                    GetFullProductImageUrlFromStringDictionary(
                      cartItem.productCode,
                    )
                  "
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                />
              </a>
            </div>
            <div class="product-info col-4">
              <div class="product-name">
                <a :href="urlMap[cartItem.productCode]?.url">{{ cartItem.skuName }}</a>
              </div>
              <div class="product-sku">{{ cartItem.skuCode }}</div>
            </div>
            <div class="product-price col-1 visible-hidden">${{ cartItem.price.toFixed(2) }}</div>
            <div class="quantity-controls col-2 visible-hidden">
              <KButton @click="minusClickedHandler(index)" class="quantity-btn">-</KButton>
              <KNumericTextBox
                v-model="cartItem.quantity"
                :min="1"
                :max="GetAvailableQty(cartItem, cartItem.quantity)"
                @change="cartItemQuantityChangedHandler($event, cartItem)"
              />
              <KButton @click="plusClickedHandler(index)" class="quantity-btn">+</KButton>
            </div>
            <div class="item-total col-1">${{ (cartItem.price * cartItem.quantity).toFixed(2) }}</div>
            <div class="delete-control col-1">
              <KButton @click="deleteItemClickedHandler(index)" class="delete-btn">×</KButton>
            </div>
          </div>
        </div>

        <!-- Cart Summary -->
        <div class="row mt-4 cart-summary">
          <div class="col-6 offset-6 text-end">
            <div class="subtotal">
              <span>{{ t('cart.Subtotal') }}:</span>
              <span>${{ cart.subtotal.toFixed(2) }}</span>
            </div>
            <div class="shipping-label">
               <span>{{ t('cart.Shipping') }}:</span>
              <span>${{ cart.shippingCost.toFixed(2) }}</span>
            </div>
            <div class="total">
              <span>{{ t('cart.Total') }}:</span>
              <span>${{ cart.total.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="row mt-4 cart-actions">
          <div class="col-12 text-end">
            <KButton
              v-if="props.ispopover"
              @click="viewCartClickedHandler"
              class="view-cart-btn"
            >
              {{ t('cart.ViewCart') }}
            </KButton>
            <KButton
              v-if="!props.ispopover"
              @click="checkoutClickedHandler"
              class="checkout-btn d-none"
            >
              {{ t('cart.Checkout') }}
            </KButton>
          </div>
        </div>
      </div>
    </div>
        <AoeCartPopupConsultantLocator

      :cart="cart"
      :ispopover="true"
      @close-popup="closePopupHandler"
    />

  </div>
</template>

<style lang="scss">
.aoe-kit-cart-detail {
  padding: $card-padding;

  .allCartItems {
    background-color: var(--vt-c-white);
    border-radius: $radius-md;
    padding: $spacing-6;
  }

  .cartItem {
    padding: $spacing-4 0;
    border-bottom: 1px solid #e0e0e0;
  }

  .product-image img {
    max-width: 100%;
    height: auto;
    border-radius: $radius-md;
  }

  .product-name {
    @include typography-body;
    @include font-weight-semibold;
    margin-bottom: 8px;
  }

  .product-name a {
    color: #333;
    text-decoration: none;
  }

  .product-sku {
    @include typography-small;
    color: #666;
    margin-bottom: 8px;
  }

  .product-price {
    @include typography-body;
    @include font-weight-semibold;
  }

  .quantity-controls {
    display: flex;
    align-items: center;
    gap: $gap-sm;
    margin: $spacing-4 0;
  }

  .quantity-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vt-c-white-soft);
    border: none;
    @include typography-body;
    border-radius: $radius-sm;
    cursor: pointer;

    &:hover {
      background: #e0e0e0;
    }
  }

  .delete-btn {
    @include typography-h4;
    @include font-weight-bold;
    color: #FF4444;
    background: none;
    border: none;
    cursor: pointer;
    padding: $spacing-1;
    margin-left: 8px;

    &:hover {
      color: #FF0000;
    }
  }

  .item-total {
    @include typography-body;
    @include font-weight-semibold;
    text-align: right;
  }

  .cart-summary {
    padding: $spacing-6 0;
    border-top: 1px solid #e0e0e0;
    margin-top: 16px;
  }

  .subtotal,
  .total, .shipping-label {
    display: flex;
    justify-content: space-between;
    @include typography-body;
    align-items: center;
    margin-bottom: 16px;
  }

  .total {
    @include typography-body-large;
    @include font-weight-semibold;
  }

  .cart-actions {
    margin-top: 24px;
  }

  .view-cart-btn,
  .checkout-btn {
    display: inline-flex;
    height: 48px;
    padding: $spacing-3 $spacing-6;
    justify-content: center;
    align-items: center;
    border-radius: $radius-sm;
    background: #0F172A;
    @include typography-body;
    @include font-weight-semibold;
    color: var(--vt-c-white);
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background: #1E293B;
    }
  }

  @media (max-width: 768px) {
    .mobileCartItems {
      display: flex;
      margin: 0 -12px;
      padding: $spacing-4 $spacing-3;
    }

    .product-info {
      padding-left: 16px;
    }

    .quantity-controls {
      margin: $spacing-3 0;
    }

    .cart-summary {
      padding: $spacing-4 0;
    }

    .cart-actions {
      margin-top: 16px;
    }
  }

  .nonMobileCartItems {
    padding: $spacing-6 0;
    align-items: center;

    .product-image {
      max-width: 120px;
    }

    .product-info {
      padding-left: 24px;
    }
  }
  .visible-hidden{visibility: hidden;}
}

</style>
